import { createSelector } from '@reduxjs/toolkit';
import type { RootStateP } from '../store';

/**
 * Selector base para permisos de usuario
 */
const selectPermissions = (state: RootStateP) => state.userInfo.Permissions;

/**
 * Selector consolidado para todos los permisos del módulo de Resultados
 * Esto reduce de 12+ selectores individuales a 1 solo
 */
export const selectResultsPermissions = createSelector(
  [selectPermissions],
  (permissions) => ({
    // Permisos de diseño y configuración
    canDesignFields: permissions?.Prm_Conf_Campos_Diseno ?? false,
    
    // Permisos de resultados básicos
    canDeleteAnalito: permissions?.Prm_Res_DelAn ?? false,
    canPrintResults: permissions?.Prm_ResImprimir ?? false,
    canPrintBarcodes: permissions?.Prm_CodBarImprimir ?? false,
    
    // Permisos de validación
    canValidate: permissions?.Prm_ResValidar ?? false,
    canInvalidate: permissions?.Prm_ResDesvalidar ?? false,
    canValidateOtherLocations: permissions?.Prm_Res_Val_OtrSd ?? false,
    canInvalidateOtherLocations: permissions?.Prm_Res_InVal_OtrSd ?? false,
    canValidateCritical: permissions?.Prm_Res_Val_Crit ?? false,
    
    // Permisos especiales
    canRetransmit: permissions?.Prm_Res_Retransmitir ?? false,
    canSeeConfidential: permissions?.Prm_Res_Conf ?? false,
    
    // Permisos de comunicación
    canSendEmail: permissions?.Prm_Res_Mail ?? false,
    canSendWhatsApp: permissions?.Prm_Res_WhatsApp ?? false,
  })
);

/**
 * Selector consolidado para permisos del módulo de Órdenes
 */
export const selectOrdersPermissions = createSelector(
  [selectPermissions],
  (permissions) => ({
    // Permisos básicos de órdenes
    canViewOrders: permissions?.Prm_Ord ?? false,
    canEditOrders: permissions?.Prm_Ord_OEr ?? false,
    
    // Permisos de notas y adjuntos
    canReadNotes: permissions?.Prm_Ord_NotasR ?? false,
    canViewAttachments: permissions?.Prm_Ord_Attachments ?? false,
    
    // Permisos de tracking y clínica
    canViewTracking: permissions?.Prm_Ord_Tracking_R ?? false,
    canViewClinicalData: permissions?.Prm_Ord_Anamnesis_R ?? false,
    
    // Permisos de remisiones
    canCreateTemporalRemissions: permissions?.Prm_Ord_Remisiones_Tmp ?? false,
    canChangeStudiesRemissions: permissions?.Prm_Ord_Remisiones_ChangeStudies ?? false,
    
    // Otros permisos
    canViewSerumLibrary: permissions?.Prm_Seroteca_R ?? false,
    canViewInformedConsents: permissions?.Prm_Ord_Consentimientos_R ?? false,
    canPrintBarcodes: permissions?.Prm_CodBarImprimir ?? false,
  })
);

/**
 * Selector consolidado para permisos generales del usuario
 */
export const selectUserPermissions = createSelector(
  [selectPermissions],
  (permissions) => ({
    // Resultados
    results: {
      canView: permissions?.Prm_Res ?? false,
      canDelete: permissions?.Prm_Res_DelAn ?? false,
      canPrint: permissions?.Prm_ResImprimir ?? false,
      canValidate: permissions?.Prm_ResValidar ?? false,
      canInvalidate: permissions?.Prm_ResDesvalidar ?? false,
    },
    
    // Órdenes
    orders: {
      canView: permissions?.Prm_Ord ?? false,
      canEdit: permissions?.Prm_Ord_OEr ?? false,
    },
    
    // Configuración
    configuration: {
      canDesignFields: permissions?.Prm_Conf_Campos_Diseno ?? false,
    },
    
    // Códigos de barras
    barcodes: {
      canPrint: permissions?.Prm_CodBarImprimir ?? false,
    },
  })
);
